import React from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, DatabaseIcon, CodeIcon, SparklesIcon, MapPinIcon, DownloadIcon, CheckCircle2Icon, TerminalIcon, MailIcon, LinkedinIcon, GlobeIcon, ArrowRightIcon, BarChart3Icon, PieChartIcon } from 'lucide-react';
export function ProductSuite() {
  return <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3331">
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12 lg:p-16 relative overflow-hidden" data-id="element-3332">
        {/* Ambient floating blob */}
        <motion.div animate={{
        x: [0, 40, -30, 0],
        y: [0, -25, 20, 0],
        scale: [1, 1.15, 0.9, 1]
      }} transition={{
        duration: 14,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-[-15%] right-[-10%] w-[40%] h-[40%] rounded-full bg-blue-100 blur-[100px] opacity-40 pointer-events-none" data-id="element-3333" />
        <div className="text-center max-w-3xl mx-auto mb-16 relative z-10" data-id="element-3334">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-3335">
            Complete local business{' '}
            <motion.span animate={{
            opacity: [1, 0.7, 1]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="text-blue-600" data-id="element-3336">
              intelligence suite
            </motion.span>
          </h2>
          <p className="text-lg text-slate-500" data-id="element-3337">
            Everything you need to find, enrich, and connect with local
            businesses worldwide
          </p>
        </div>

        {/* Bento Grid — 5:3:3:5 ratio */}
        <div className="grid grid-cols-1 md:grid-cols-8 gap-6" data-id="element-3338">
          {/* 1. Local businesses extractor (col-span-5) */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5
        }} className="md:col-span-5 rounded-3xl border border-slate-200 bg-white overflow-hidden flex flex-col group hover:border-blue-200 hover:shadow-2xl hover:shadow-blue-600/10 hover:-translate-y-2 transition-all duration-300 relative" data-id="element-3339">
            <div className="absolute inset-0 bg-blue-50/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-3340"></div>
            <div className="p-8 md:p-10 flex-1 flex flex-col lg:flex-row gap-8 items-center relative z-10" data-id="element-3341">
              <div className="flex-1 space-y-6" data-id="element-3342">
                <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center group-hover:bg-blue-50 transition-colors border border-slate-100" data-id="element-3343">
                  <motion.div whileHover={{
                  rotate: 15,
                  scale: 1.1
                }} transition={{
                  duration: 0.2
                }} data-id="element-3344">
                    <SearchIcon className="w-6 h-6 text-slate-700 group-hover:text-blue-600 transition-colors" data-id="element-3345" />
                  </motion.div>
                </div>
                <div data-id="element-3346">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors" data-id="element-3347">
                    Local businesses extractor
                  </h3>
                  <p className="text-slate-500 leading-relaxed mb-6" data-id="element-3348">
                    Scrape business information from Google Maps into CSV/XLSX
                    files. Extract verified contacts, ratings, and business
                    metadata in minutes.
                  </p>
                  <ul className="space-y-3 mb-8" data-id="element-3349">
                    {['Real-time extraction', 'CSV/Excel export', 'Unlimited searches'].map((bullet, i) => <li key={i} className="flex items-center gap-3 text-sm font-medium text-slate-700" data-id="element-3350">
                        <div className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-3351">
                          <CheckCircle2Icon className="w-3 h-3" data-id="element-3352" />
                        </div>
                        {bullet}
                      </li>)}
                  </ul>
                  <a href="#/google-maps-scraper" className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 rounded-full font-bold text-sm text-slate-900 hover:border-blue-300 hover:text-blue-600 transition-colors shadow-sm group-hover:shadow" data-id="element-3353">
                    Start extracting <ArrowRightIcon className="w-4 h-4" data-id="element-3354" />
                  </a>
                </div>
              </div>

              {/* UI Mockup */}
              <div className="w-full lg:w-[320px] shrink-0 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 relative overflow-hidden group-hover:-translate-y-1 transition-transform duration-500" data-id="element-3355">
                <div className="flex items-center gap-2 mb-4 pb-4 border-b border-slate-100" data-id="element-3356">
                  <div className="flex-1 bg-slate-50 rounded-lg px-3 py-2 flex items-center gap-2 border border-slate-100" data-id="element-3357">
                    <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-3358" />
                    <span className="text-xs text-slate-600 font-medium" data-id="element-3359">
                      Restaurants in Austin, TX
                    </span>
                  </div>
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-3360">
                    <DownloadIcon className="w-4 h-4" data-id="element-3361" />
                  </div>
                </div>
                <div className="space-y-3 relative" data-id="element-3362">
                  <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent z-10" data-id="element-3363" />
                  {[1, 2, 3].map(i => <div key={i} className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-3364">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0" data-id="element-3365">
                        <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-3366" />
                      </div>
                      <div data-id="element-3367">
                        <div className="text-sm font-bold text-slate-900" data-id="element-3368">
                          Austin Taco Co. {i}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-3369">
                          4.8 ★ (1.2k reviews) • Mexican
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </motion.div>

          {/* 2. Local business database (col-span-3, light blue bg) */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="md:col-span-3 rounded-3xl border border-blue-100 bg-blue-50/40 overflow-hidden flex flex-col group hover:border-blue-200 hover:shadow-2xl hover:shadow-blue-600/10 hover:-translate-y-2 transition-all duration-300 relative" data-id="element-3370">
            <div className="absolute inset-0 bg-blue-50/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-3371"></div>
            <div className="p-8 md:p-10 flex-1 flex flex-col relative z-10" data-id="element-3372">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-50 transition-colors border border-blue-100" data-id="element-3373">
                <motion.div whileHover={{
                rotate: 15,
                scale: 1.1
              }} transition={{
                duration: 0.2
              }} data-id="element-3374">
                  <DatabaseIcon className="w-6 h-6 text-blue-600 group-hover:text-blue-700 transition-colors" data-id="element-3375" />
                </motion.div>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors" data-id="element-3376">
                Local business database
              </h3>
              <p className="text-slate-500 leading-relaxed mb-8" data-id="element-3377">
                Download ready-to-use business datasets by category and
                location. Pre-built datasets updated weekly.
              </p>

              {/* UI Mockup */}
              <div className="mt-auto bg-white rounded-2xl border border-blue-100 shadow-sm p-4 space-y-3 mb-8 group-hover:-translate-y-1 transition-transform duration-500" data-id="element-3378">
                {[{
                name: 'TechFlow Solutions',
                type: 'Software',
                verified: true
              }, {
                name: 'Apex Dental',
                type: 'Healthcare',
                verified: true
              }, {
                name: 'Summit Roofing',
                type: 'Contractor',
                verified: false
              }].map((row, i) => <div key={i} className="bg-white rounded-xl p-3 border border-blue-100/50 shadow-sm flex items-center justify-between" data-id="element-3379">
                    <div className="flex items-center gap-2" data-id="element-3380">
                      <div className="w-6 h-6 rounded bg-blue-100 text-blue-600 flex items-center justify-center text-[10px] font-bold" data-id="element-3381">
                        {row.name[0]}
                      </div>
                      <div data-id="element-3382">
                        <div className="text-xs font-bold text-slate-900" data-id="element-3383">
                          {row.name}
                        </div>
                        <div className="text-[9px] text-slate-400" data-id="element-3384">
                          {row.type}
                        </div>
                      </div>
                    </div>
                    {row.verified && <CheckCircle2Icon className="w-3.5 h-3.5 text-green-500" data-id="element-3385" />}
                  </div>)}
              </div>

              <div className="mt-auto" data-id="element-3386">
                <ul className="space-y-2 mb-6" data-id="element-3387">
                  {['200M+ records', '97-99% accuracy', 'Monthly updates'].map((bullet, i) => <li key={i} className="flex items-center gap-2 text-xs font-medium text-slate-600" data-id="element-3388">
                        <span className="w-1 h-1 rounded-full bg-blue-600" data-id="element-3389" />
                        {bullet}
                      </li>)}
                </ul>
                <a href="#/local-business-database" className="inline-flex items-center justify-center w-full gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-full font-bold text-sm text-slate-900 hover:border-blue-300 hover:text-blue-600 transition-colors shadow-sm" data-id="element-3390">
                  Explore database
                </a>
              </div>
            </div>
          </motion.div>

          {/* 3. Local business API (col-span-3, Dark) */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="md:col-span-3 rounded-3xl border border-slate-800 bg-slate-900 overflow-hidden flex flex-col group hover:border-blue-500 hover:shadow-2xl hover:shadow-blue-500/20 hover:-translate-y-2 transition-all duration-300 relative" data-id="element-3391">
            <div className="absolute inset-0 bg-blue-900/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-3392"></div>
            <div className="p-8 md:p-10 flex-1 flex flex-col relative z-10" data-id="element-3393">
              <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-900/50 transition-colors border border-slate-700" data-id="element-3394">
                <motion.div whileHover={{
                rotate: 15,
                scale: 1.1
              }} transition={{
                duration: 0.2
              }} data-id="element-3395">
                  <CodeIcon className="w-6 h-6 text-slate-300 group-hover:text-blue-400 transition-colors" data-id="element-3396" />
                </motion.div>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors" data-id="element-3397">
                Local business API
              </h3>
              <p className="text-slate-400 leading-relaxed mb-8" data-id="element-3398">
                Integrate our 200M+ business database directly into your product
                or internal tools.
              </p>

              {/* UI Mockup */}
              <div className="mt-auto group-hover:-translate-y-1 transition-transform duration-500" data-id="element-3399">
                <div className="bg-slate-950 rounded-2xl border border-slate-800 overflow-hidden" data-id="element-3400">
                  <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-800 bg-slate-900/50" data-id="element-3401">
                    <div className="w-2 h-2 rounded-full bg-red-500/80" data-id="element-3402" />
                    <div className="w-2 h-2 rounded-full bg-yellow-500/80" data-id="element-3403" />
                    <div className="w-2 h-2 rounded-full bg-green-500/80" data-id="element-3404" />
                    <span className="text-[9px] text-slate-500 font-mono ml-2 flex items-center gap-1" data-id="element-3405">
                      <TerminalIcon className="w-3 h-3" data-id="element-3406" /> api.openmart.com
                    </span>
                  </div>
                  <div className="p-4 font-mono text-[11px] leading-relaxed" data-id="element-3407">
                    <div className="text-blue-400" data-id="element-3408">
                      GET{' '}
                      <span className="text-slate-300" data-id="element-3409">
                        /v1/businesses/search
                      </span>
                    </div>
                    <div className="text-slate-500 mt-2" data-id="element-3410">{'{'}</div>
                    <div className="pl-4 text-slate-300" data-id="element-3411">
                      <span className="text-sky-300" data-id="element-3412">"status"</span>:{' '}
                      <span className="text-green-400" data-id="element-3413">200</span>,<br data-id="element-3414" />
                      <span className="text-sky-300" data-id="element-3415">"data"</span>: {'[{'}
                      <br data-id="element-3416" />
                      <div className="pl-4" data-id="element-3417">
                        <span className="text-sky-300" data-id="element-3418">"name"</span>:{' '}
                        <span className="text-amber-300" data-id="element-3419">"Austin Taco"</span>,
                        <br data-id="element-3420" />
                        <span className="text-sky-300" data-id="element-3421">"emails"</span>:{' '}
                        <span className="text-amber-300" data-id="element-3422">["info@..."]</span>
                      </div>
                      {'}]'}
                    </div>
                    <div className="text-slate-500" data-id="element-3423">{'}'}</div>
                  </div>
                </div>
              </div>

              <div className="mt-auto pt-6" data-id="element-3424">
                <ul className="space-y-2 mb-6" data-id="element-3425">
                  {['RESTful API', 'Real-time data', '99.9% uptime'].map((bullet, i) => <li key={i} className="flex items-center gap-2 text-xs font-medium text-slate-400" data-id="element-3426">
                        <span className="w-1 h-1 rounded-full bg-blue-500" data-id="element-3427" />
                        {bullet}
                      </li>)}
                </ul>
                <a href="#/local-business-api" className="inline-flex items-center justify-center w-full gap-2 px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-full font-bold text-sm text-white hover:bg-slate-700 hover:border-slate-600 transition-colors shadow-sm" data-id="element-3428">
                  View API docs
                </a>
              </div>
            </div>
          </motion.div>

          {/* 4. Local business enrichment (col-span-5) */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="md:col-span-5 rounded-3xl border border-slate-200 bg-white overflow-hidden flex flex-col group hover:border-blue-200 hover:shadow-2xl hover:shadow-blue-600/10 hover:-translate-y-2 transition-all duration-300 relative" data-id="element-3429">
            <div className="absolute inset-0 bg-blue-50/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-3430"></div>
            <div className="p-8 md:p-10 flex-1 flex flex-col lg:flex-row-reverse gap-8 items-center relative z-10" data-id="element-3431">
              <div className="flex-1 space-y-6" data-id="element-3432">
                <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center group-hover:bg-blue-50 transition-colors border border-slate-100" data-id="element-3433">
                  <motion.div whileHover={{
                  rotate: 15,
                  scale: 1.1
                }} transition={{
                  duration: 0.2
                }} data-id="element-3434">
                    <SparklesIcon className="w-6 h-6 text-slate-700 group-hover:text-blue-600 transition-colors" data-id="element-3435" />
                  </motion.div>
                </div>
                <div data-id="element-3436">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors" data-id="element-3437">
                    Local business enrichment
                  </h3>
                  <p className="text-slate-500 leading-relaxed mb-6" data-id="element-3438">
                    Append missing data points to your existing business lists.
                    Find verified owner emails, direct phone numbers, and social
                    profiles instantly.
                  </p>
                  <ul className="space-y-3 mb-8" data-id="element-3439">
                    {['Verified owner emails', 'Direct phone numbers', 'Social media profiles'].map((bullet, i) => <li key={i} className="flex items-center gap-3 text-sm font-medium text-slate-700" data-id="element-3440">
                        <div className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-3441">
                          <CheckCircle2Icon className="w-3 h-3" data-id="element-3442" />
                        </div>
                        {bullet}
                      </li>)}
                  </ul>
                  <a href="#/local-business-enrichment" className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 rounded-full font-bold text-sm text-slate-900 hover:border-blue-300 hover:text-blue-600 transition-colors shadow-sm group-hover:shadow" data-id="element-3443">
                    Start enriching <ArrowRightIcon className="w-4 h-4" data-id="element-3444" />
                  </a>
                </div>
              </div>

              {/* UI Mockup */}
              <div className="w-full lg:w-[340px] shrink-0 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 relative overflow-hidden group-hover:-translate-y-1 transition-transform duration-500" data-id="element-3445">
                <div className="flex items-center justify-between mb-4" data-id="element-3446">
                  <div className="flex items-center gap-3" data-id="element-3447">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold" data-id="element-3448">
                      SJ
                    </div>
                    <div data-id="element-3449">
                      <div className="text-sm font-bold text-slate-900" data-id="element-3450">
                        Sarah Jenkins
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-3451">
                        TechFlow Solutions
                      </div>
                    </div>
                  </div>
                  <div className="px-2.5 py-1 bg-purple-50 text-purple-600 rounded-md text-[10px] font-bold flex items-center gap-1" data-id="element-3452">
                    <SparklesIcon className="w-3 h-3" data-id="element-3453" /> Enriched
                  </div>
                </div>

                <div className="space-y-2.5" data-id="element-3454">
                  <div className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 border border-slate-100" data-id="element-3455">
                    <MailIcon className="w-4 h-4 text-slate-400 shrink-0" data-id="element-3456" />
                    <div className="flex-1 text-sm text-slate-700 font-medium" data-id="element-3457">
                      sarah@techflow.io
                    </div>
                    <CheckCircle2Icon className="w-4 h-4 text-green-500 shrink-0" data-id="element-3458" />
                  </div>
                  <div className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 border border-slate-100" data-id="element-3459">
                    <LinkedinIcon className="w-4 h-4 text-slate-400 shrink-0" data-id="element-3460" />
                    <div className="flex-1 text-sm text-blue-600 font-medium truncate" data-id="element-3461">
                      linkedin.com/in/sarahj
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 border border-slate-100" data-id="element-3462">
                    <GlobeIcon className="w-4 h-4 text-slate-400 shrink-0" data-id="element-3463" />
                    <div className="flex-1 text-sm text-slate-700 font-medium" data-id="element-3464">
                      techflow.io
                    </div>
                  </div>
                </div>

                <motion.div animate={{
                y: [0, 200, 0]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'linear'
              }} className="absolute top-0 left-0 right-0 h-0.5 bg-blue-400 opacity-30" data-id="element-3465" />
              </div>
            </div>
          </motion.div>

          {/* 5. Openclaw for sales (full-width) */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} className="md:col-span-8 rounded-3xl p-[2px] overflow-hidden flex flex-col group hover:shadow-2xl hover:shadow-blue-600/15 hover:-translate-y-2 transition-all duration-300 relative" style={{
          background: 'linear-gradient(135deg, #156ef6, #4f94fe, #06b6d4, #156ef6)',
          backgroundSize: '300% 300%'
        }} data-id="element-3466">
            <motion.div animate={{
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'linear'
          }} className="absolute inset-0 rounded-3xl" style={{
            background: 'linear-gradient(135deg, #156ef6, #4f94fe, #06b6d4, #156ef6)',
            backgroundSize: '300% 300%'
          }} data-id="element-3467" />
            <div className="relative bg-white rounded-[calc(1.5rem-2px)] flex-1 flex flex-col overflow-hidden z-10" data-id="element-3468">
              <div className="absolute inset-0 bg-blue-50/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-3469"></div>
              <div className="p-8 md:p-10 flex-1 flex flex-col lg:flex-row gap-8 items-center relative z-10" data-id="element-3470">
                {/* Left: Text */}
                <div className="flex-1 space-y-6" data-id="element-3471">
                  <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center group-hover:bg-blue-50 transition-colors border border-slate-100" data-id="element-3472">
                    <motion.div whileHover={{
                    rotate: 15,
                    scale: 1.1
                  }} transition={{
                    duration: 0.2
                  }} data-id="element-3473">
                      <BarChart3Icon className="w-6 h-6 text-slate-700 group-hover:text-blue-600 transition-colors" data-id="element-3474" />
                    </motion.div>
                  </div>
                  <div data-id="element-3475">
                    <div className="flex items-center gap-3 mb-3" data-id="element-3476">
                      <h3 className="text-2xl font-bold text-slate-900 group-hover:text-blue-600 transition-colors" data-id="element-3477">
                        Openclaw for sales
                      </h3>
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-600 text-white rounded-md text-[10px] font-bold" data-id="element-3478">
                        New
                      </span>
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-600 rounded-md text-[10px] font-bold border border-blue-100" data-id="element-3479">
                        Enterprise
                      </span>
                    </div>
                    <p className="text-slate-500 leading-relaxed mb-6" data-id="element-3480">
                      AI-powered local business research and sales intelligence.
                      Ask a question about any market and get ranked results,
                      visual analysis, and exportable lead lists in minutes.
                    </p>
                    <ul className="space-y-3 mb-8" data-id="element-3481">
                      {['Natural language market queries', 'Instant visual analytics & reports', 'Exportable outreach-ready lead lists'].map((bullet, i) => <li key={i} className="flex items-center gap-3 text-sm font-medium text-slate-700" data-id="element-3482">
                          <div className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-3483">
                            <CheckCircle2Icon className="w-3 h-3" data-id="element-3484" />
                          </div>
                          {bullet}
                        </li>)}
                    </ul>
                    <a href="#/openclaw" className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 rounded-full font-bold text-sm text-slate-900 hover:border-blue-300 hover:text-blue-600 transition-colors shadow-sm group-hover:shadow" data-id="element-3485">
                      Try Openclaw for sales{' '}
                      <ArrowRightIcon className="w-4 h-4" data-id="element-3486" />
                    </a>
                  </div>
                </div>

                {/* Right: Analytics Illustration */}
                <div className="w-full lg:w-[480px] shrink-0 space-y-3 group-hover:-translate-y-1 transition-transform duration-500" data-id="element-3487">
                  {/* Query Input */}
                  <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3 shadow-sm" data-id="element-3488">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center" data-id="element-3489">
                      <SparklesIcon className="w-4 h-4 text-blue-600" data-id="element-3490" />
                    </div>
                    <div className="flex-1" data-id="element-3491">
                      <div className="text-xs text-slate-400 font-medium mb-0.5" data-id="element-3492">
                        Query
                      </div>
                      <div className="text-sm font-medium text-slate-700" data-id="element-3493">
                        Top Asian grocery chains in Canada
                      </div>
                    </div>
                    <div className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold" data-id="element-3494">
                      Analyze
                    </div>
                  </div>

                  {/* Charts Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-3495">
                    {/* Bar Chart */}
                    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-id="element-3496">
                      <div className="flex items-center gap-1.5 mb-4" data-id="element-3497">
                        <BarChart3Icon className="w-3.5 h-3.5 text-slate-400" data-id="element-3498" />
                        <span className="text-[11px] font-bold text-slate-700" data-id="element-3499">
                          Top Chains — Location Count
                        </span>
                      </div>
                      <div className="space-y-2" data-id="element-3500">
                        {[{
                        name: 'H Mart',
                        value: 92,
                        count: '37'
                      }, {
                        name: 'Galleria',
                        value: 28,
                        count: '11'
                      }, {
                        name: 'T&T',
                        value: 25,
                        count: '10'
                      }, {
                        name: 'Seafood City',
                        value: 18,
                        count: '7'
                      }, {
                        name: 'Kim Phat',
                        value: 12,
                        count: '5'
                      }, {
                        name: 'Ethnic SM',
                        value: 8,
                        count: '3'
                      }].map((item, i) => <div key={i} className="flex items-center gap-2" data-id="element-3501">
                            <span className="text-[9px] text-slate-500 w-14 truncate text-right shrink-0" data-id="element-3502">
                              {item.name}
                            </span>
                            <div className="flex-1 h-3.5 bg-slate-50 rounded-sm overflow-hidden" data-id="element-3503">
                              <motion.div initial={{
                            width: 0
                          }} whileInView={{
                            width: `${item.value}%`
                          }} viewport={{
                            once: true
                          }} transition={{
                            duration: 0.8,
                            delay: 0.3 + i * 0.08
                          }} className="h-full rounded-sm" style={{
                            backgroundColor: i === 0 ? '#156ef6' : i === 1 ? '#4f94fe' : i === 2 ? '#7bc5fe' : i === 3 ? '#a5d4fe' : i === 4 ? '#c7e2ff' : '#e0eeff'
                          }} data-id="element-3504" />
                            </div>
                            <span className="text-[9px] font-bold text-slate-600 w-5 shrink-0" data-id="element-3505">
                              {item.count}
                            </span>
                          </div>)}
                      </div>
                    </div>

                    {/* Pie Chart */}
                    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col" data-id="element-3506">
                      <div className="flex items-center gap-1.5 mb-4" data-id="element-3507">
                        <PieChartIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-3508" />
                        <span className="text-[11px] font-bold text-slate-700" data-id="element-3509">
                          By Target Audience
                        </span>
                      </div>
                      <div className="flex-1 flex items-center justify-center" data-id="element-3510">
                        <motion.div initial={{
                        scale: 0,
                        opacity: 0,
                        rotate: -180
                      }} whileInView={{
                        scale: 1,
                        opacity: 1,
                        rotate: 0
                      }} viewport={{
                        once: true
                      }} transition={{
                        duration: 0.8,
                        delay: 0.4,
                        type: 'spring',
                        stiffness: 80
                      }} className="relative w-24 h-24 rounded-full shadow-sm" style={{
                        background: 'conic-gradient(#156ef6 0% 33%, #4f94fe 33% 53%, #7bc5fe 53% 66%, #0a1c50 66% 73%, #1260d4 73% 93%, #a5d4fe 93% 100%)'
                      }} data-id="element-3511">
                          <div className="absolute inset-3 bg-white rounded-full flex items-center justify-center" data-id="element-3512">
                            <span className="text-[10px] font-bold text-slate-600" data-id="element-3513">
                              6 segments
                            </span>
                          </div>
                        </motion.div>
                      </div>
                      <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 mt-3" data-id="element-3514">
                        {[{
                        label: 'Chinese',
                        color: '#156ef6',
                        pct: '33%'
                      }, {
                        label: 'Korean',
                        color: '#4f94fe',
                        pct: '20%'
                      }, {
                        label: 'Vietnamese',
                        color: '#7bc5fe',
                        pct: '13%'
                      }, {
                        label: 'Filipino',
                        color: '#0a1c50',
                        pct: '7%'
                      }, {
                        label: 'South Asian',
                        color: '#1260d4',
                        pct: '7%'
                      }, {
                        label: 'Pan-Asian',
                        color: '#a5d4fe',
                        pct: '20%'
                      }].map((seg, i) => <div key={i} className="flex items-center gap-1" data-id="element-3515">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{
                          backgroundColor: seg.color
                        }} data-id="element-3516"></span>
                            <span className="text-[8px] text-slate-500 font-medium" data-id="element-3517">
                              {seg.label} {seg.pct}
                            </span>
                          </div>)}
                      </div>
                    </div>
                  </div>

                  {/* Bottom result hint */}
                  <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm flex items-center justify-between" data-id="element-3518">
                    <div className="flex items-center gap-2" data-id="element-3519">
                      <DownloadIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-3520" />
                      <span className="text-[11px] font-medium text-slate-600" data-id="element-3521">
                        Saved as{' '}
                        <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-[10px] text-slate-700" data-id="element-3522">
                          top_asian_grocery_chains_canada.csv
                        </span>
                      </span>
                    </div>
                    <span className="text-[10px] font-bold text-blue-600" data-id="element-3523">
                      48 results
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}